import { useMemo, useState } from "react";
import { AsyncEntity } from "../../work-type-shared/async-entity-picker";
import {
  BaseFlexScheduleRequest,
  BaseWorkPolicyDetail,
  PolicyKind,
  WorkPolicyDetail,
  WorkPolicyRequest,
  WorkPolicySummary,
  WorkType
} from "../../../api/work-policy-api.types";
import {
  calendarDateRange,
  clampCalendarRangeEnd,
  isCalendarDate,
  isCalendarRangeWithinMonths,
  localCalendarDate,
  MAX_FLEXIBLE_DATE_RANGE_MONTHS
} from "../../work-type-shared/calendar-date-range";
import { validateTypeSpecificWorkPolicy } from "../work-policy.validation";

export interface WorkPolicyFormValue {
  workPolicyId?: number;
  policyKind: PolicyKind;
  workType: WorkType;
  profileName: string;
  dailyBaseHours: string;
  weeklyBaseHours: string;
  weeklyOvertimeLimit: string;
  breakCalculationEnabled: boolean;
  breakWorkMinutes: string;
  breakMinutes: string;
  nightStart: string;
  nightEnd: string;
  exposureStatus: "VISIBLE" | "HIDDEN";
  targetType: "all" | "dept" | "user";
  selectedDepartments: AsyncEntity[];
  selectedEmployees: AsyncEntity[];
  staggeredStartTimes: string[];
  flexibleMode: "DAILY_DATE" | "WEEKLY_PATTERN";
  selectiveCoreEnabled: boolean;
  selectiveCoreStart: string;
  selectiveCoreEnd: string;
  settlementPreviewDate: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  baseDefaultStartTime: string;
  schedule: BaseFlexScheduleRequest[];
  scheduleSelectedKeys: string[];
}

export type WorkPolicyFormErrors = Partial<Record<keyof WorkPolicyFormValue, string>>;

function clock(value?: string | null) {
  return value ? value.slice(0, 5) : "";
}

export function createEmptyWorkPolicyForm(
  policyKind: PolicyKind
): WorkPolicyFormValue {
  const today = policyKind === "BASE" ? localCalendarDate() : "";
  return {
    policyKind,
    workType: "staggered",
    profileName: "",
    dailyBaseHours: "8",
    weeklyBaseHours: "40",
    weeklyOvertimeLimit: "12",
    breakCalculationEnabled: true,
    breakWorkMinutes: "240",
    breakMinutes: "30",
    nightStart: "22:00",
    nightEnd: "06:00",
    exposureStatus: "VISIBLE",
    targetType: "all",
    selectedDepartments: [],
    selectedEmployees: [],
    staggeredStartTimes: ["09:00"],
    flexibleMode: "DAILY_DATE",
    selectiveCoreEnabled: false,
    selectiveCoreStart: "",
    selectiveCoreEnd: "",
    settlementPreviewDate: "",
    effectiveStartDate: today,
    effectiveEndDate: today,
    baseDefaultStartTime: "09:00",
    schedule: [],
    scheduleSelectedKeys: []
  };
}

function defaultDailyScheduleRow(workDate: string): BaseFlexScheduleRequest {
  return {
    workDate,
    weekdayNo: null,
    startTime: "09:00",
    plannedWorkMinutes: 480,
    breakWaiver: false,
    workday: true
  };
}

export function reconcileDailyDateSchedule(
  startDate: string,
  endDate: string,
  schedule: BaseFlexScheduleRequest[]
) {
  if (
    !isCalendarDate(startDate) ||
    !isCalendarDate(endDate) ||
    !isCalendarRangeWithinMonths(
      startDate,
      endDate,
      MAX_FLEXIBLE_DATE_RANGE_MONTHS
    )
  ) {
    return [];
  }
  const existingRows = new Map(
    schedule
      .filter(row => row.workDate)
      .map(row => [row.workDate as string, row] as const)
  );
  return calendarDateRange(startDate, endDate).map(
    workDate => existingRows.get(workDate) ?? defaultDailyScheduleRow(workDate)
  );
}

export function updateWorkPolicyFormField<K extends keyof WorkPolicyFormValue>(
  current: WorkPolicyFormValue,
  field: K,
  fieldValue: WorkPolicyFormValue[K]
) {
  const next = { ...current, [field]: fieldValue };
  if (
    current.policyKind === "BASE" &&
    (field === "effectiveStartDate" || field === "effectiveEndDate") &&
    isCalendarDate(next.effectiveStartDate) &&
    isCalendarDate(next.effectiveEndDate)
  ) {
    if (next.effectiveEndDate < next.effectiveStartDate) {
      next.effectiveEndDate = next.effectiveStartDate;
    }
    next.effectiveEndDate = clampCalendarRangeEnd(
      next.effectiveStartDate,
      next.effectiveEndDate,
      MAX_FLEXIBLE_DATE_RANGE_MONTHS
    );
    if (next.workType === "flexible" && next.flexibleMode === "DAILY_DATE") {
      next.schedule = reconcileDailyDateSchedule(
        next.effectiveStartDate,
        next.effectiveEndDate,
        next.schedule
      );
      const dateKeys = new Set(next.schedule.map(row => row.workDate));
      next.scheduleSelectedKeys = next.scheduleSelectedKeys.filter(key =>
        dateKeys.has(key)
      );
    }
  }
  return next;
}

export function workPolicyDetailToForm(
  detail: WorkPolicyDetail
): WorkPolicyFormValue {
  const common: WorkPolicyFormValue = {
    ...createEmptyWorkPolicyForm(detail.policyKind),
    workPolicyId: detail.workPolicyId,
    workType: detail.workType,
    profileName: detail.profileName,
    dailyBaseHours: String(detail.dailyBaseHours),
    weeklyBaseHours: String(detail.weeklyBaseHours),
    weeklyOvertimeLimit: String(detail.weeklyOvertimeLimit),
    breakCalculationEnabled: detail.breakCalculationEnabled,
    breakWorkMinutes: String(detail.breakWorkMinutes),
    breakMinutes: String(detail.breakMinutes),
    nightStart: clock(detail.nightStart),
    nightEnd: clock(detail.nightEnd),
    flexibleMode: detail.flexibleMode ?? "DAILY_DATE",
    selectiveCoreEnabled: detail.selectiveCoreEnabled,
    selectiveCoreStart: clock(detail.selectiveCoreStart),
    selectiveCoreEnd: clock(detail.selectiveCoreEnd)
  };

  if (detail.policyKind === "APPROVAL") {
    return {
      ...common,
      exposureStatus: detail.exposureStatus,
      targetType: detail.targetType,
      selectedDepartments: detail.targets
        .filter(target => target.targetType === "dept")
        .map(target => ({
          id: target.targetValue,
          name: `부서 ${target.targetValue}`,
          description: "저장된 대상 부서"
        })),
      selectedEmployees: detail.targets
        .filter(target => target.targetType === "user")
        .map(target => ({
          id: target.targetValue,
          name: target.targetValue,
          description: "저장된 대상 직원"
        })),
      staggeredStartTimes:
        detail.staggeredTimes.length > 0
          ? detail.staggeredTimes.map(value => clock(value.startTime))
          : ["09:00"]
    };
  }

  return {
    ...common,
    effectiveStartDate: detail.effectiveStartDate,
    effectiveEndDate: detail.effectiveEndDate,
    settlementPreviewDate: detail.effectiveStartDate,
    baseDefaultStartTime: clock(detail.defaultStartTime),
    schedule: detail.schedule.map(row => ({
      workDate: row.workDate,
      weekdayNo: row.weekdayNo,
      startTime: clock(row.startTime) || null,
      plannedWorkMinutes: row.plannedWorkMinutes,
      breakWaiver: row.breakWaiver,
      workday: row.workday
    })),
    scheduleSelectedKeys: []
  };
}

export function validateWorkPolicyForm(
  value: WorkPolicyFormValue,
  summaries: WorkPolicySummary[]
): WorkPolicyFormErrors {
  const errors: WorkPolicyFormErrors = {};
  const daily = Number(value.dailyBaseHours);
  const weekly = Number(value.weeklyBaseHours);
  const overtime = Number(value.weeklyOvertimeLimit);
  const breakWork = Number(value.breakWorkMinutes);
  const breakMinutes = Number(value.breakMinutes);

  if (!value.profileName.trim()) {
    errors.profileName = "근무정책 명칭을 입력하세요.";
  } else if (value.profileName.trim().length > 100) {
    errors.profileName = "근무정책 명칭은 100자 이하로 입력하세요.";
  } else if (
    summaries.some(
      summary =>
        summary.workPolicyId !== value.workPolicyId &&
        summary.profileName.trim().toLocaleLowerCase() ===
          value.profileName.trim().toLocaleLowerCase()
    )
  ) {
    errors.profileName = "근무정책 명칭이 중복됩니다.";
  }
  if (!Number.isFinite(daily) || daily <= 0 || daily > 24) {
    errors.dailyBaseHours = "일 기준시간은 24 이하이며 0보다 커야 합니다.";
  }
  if (!Number.isFinite(weekly) || weekly <= 0 || weekly > 168) {
    errors.weeklyBaseHours = "주 기준시간은 0보다 크고 168 이하여야 합니다.";
  } else if (Number.isFinite(daily) && weekly < daily) {
    errors.weeklyBaseHours = "주 기준시간은 일 기준시간보다 작을 수 없습니다.";
  }
  if (!Number.isFinite(overtime) || overtime < 0 || overtime > 168) {
    errors.weeklyOvertimeLimit = "주 연장 한도는 0~168시간이어야 합니다.";
  }
  if (
    !Number.isInteger(breakWork) ||
    breakWork < (value.breakCalculationEnabled ? 30 : 0) ||
    breakWork > 1440 ||
    breakWork % 30 !== 0
  ) {
    errors.breakWorkMinutes = "휴게 기준 근로시간은 0.5~24시간 범위에서 0.5시간 단위로 입력하세요.";
  }
  if (
    !Number.isInteger(breakMinutes) ||
    breakMinutes < (value.breakCalculationEnabled ? 1 : 0) ||
    breakMinutes > 1440
  ) {
    errors.breakMinutes = "휴게시간은 0~1440분 정수여야 합니다.";
  }
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value.nightStart)) {
    errors.nightStart = "야간 시작을 HH:mm 형식으로 입력하세요.";
  }
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value.nightEnd)) {
    errors.nightEnd = "야간 종료를 HH:mm 형식으로 입력하세요.";
  } else if (value.nightStart === value.nightEnd) {
    errors.nightEnd = "야간 시작과 종료는 달라야 합니다.";
  }
  if (value.policyKind === "APPROVAL") {
    if (value.targetType === "dept" && value.selectedDepartments.length === 0) {
      errors.selectedDepartments = "대상 부서를 한 개 이상 선택하세요.";
    }
    if (value.targetType === "user" && value.selectedEmployees.length === 0) {
      errors.selectedEmployees = "대상 직원을 한 명 이상 선택하세요.";
    }
  }
  return { ...errors, ...validateTypeSpecificWorkPolicy(value) };
}

export function buildWorkPolicyRequest(
  value: WorkPolicyFormValue
): WorkPolicyRequest {
  const common = {
    workType: value.workType,
    profileName: value.profileName.trim(),
    dailyBaseHours: Number(value.dailyBaseHours),
    weeklyBaseHours: Number(value.weeklyBaseHours),
    weeklyOvertimeLimit: Number(value.weeklyOvertimeLimit),
    breakCalculationEnabled: value.breakCalculationEnabled,
    breakWorkMinutes: Number(value.breakWorkMinutes),
    breakMinutes: Number(value.breakMinutes),
    nightStart: value.nightStart,
    nightEnd: value.nightEnd
  };

  if (value.policyKind === "APPROVAL") {
    const approval = {
      ...common,
      policyKind: "APPROVAL" as const,
      exposureStatus: value.exposureStatus,
      targetType: value.targetType,
      departmentIds:
        value.targetType === "dept"
          ? value.selectedDepartments.map(entity => Number(entity.id))
          : [],
      employeeIds:
        value.targetType === "user"
          ? value.selectedEmployees.map(entity => entity.id)
          : []
    };
    if (value.workType === "staggered") {
      return { ...approval, staggeredStartTimes: value.staggeredStartTimes };
    }
    if (value.workType === "flexible") {
      return { ...approval, flexibleMode: value.flexibleMode };
    }
    return {
      ...approval,
      selectiveCoreEnabled: value.selectiveCoreEnabled,
      ...(value.selectiveCoreEnabled
        ? {
            selectiveCoreStart: value.selectiveCoreStart,
            selectiveCoreEnd: value.selectiveCoreEnd
          }
        : {})
    };
  }

  const base = {
    ...common,
    policyKind: "BASE" as const,
    effectiveStartDate: value.effectiveStartDate,
    effectiveEndDate: value.effectiveEndDate
  };
  if (value.workType === "staggered") {
    return { ...base, baseDefaultStartTime: value.baseDefaultStartTime };
  }
  if (value.workType === "flexible") {
    return {
      ...base,
      flexibleMode: value.flexibleMode,
      schedule: value.schedule.map(row =>
        row.workday
          ? row
          : {
              ...row,
              startTime: null,
              plannedWorkMinutes: 0,
              breakWaiver: false
            }
      )
    };
  }
  return {
    ...base,
    selectiveCoreEnabled: value.selectiveCoreEnabled,
    ...(value.selectiveCoreEnabled
      ? {
          selectiveCoreStart: value.selectiveCoreStart,
          selectiveCoreEnd: value.selectiveCoreEnd
        }
      : {})
  };
}

export function resetWorkPolicyTypeDetails(
  value: WorkPolicyFormValue,
  workType: WorkType
): WorkPolicyFormValue {
  return {
    ...value,
    workType,
    staggeredStartTimes: workType === "staggered" ? ["09:00"] : [],
    flexibleMode: "DAILY_DATE",
    schedule:
      workType === "flexible" && value.policyKind === "BASE"
        ? reconcileDailyDateSchedule(
            value.effectiveStartDate,
            value.effectiveEndDate,
            []
          )
        : [],
    scheduleSelectedKeys: [],
    baseDefaultStartTime: "09:00",
    selectiveCoreEnabled: false,
    selectiveCoreStart: "",
    selectiveCoreEnd: "",
    settlementPreviewDate:
      workType === "selective" && value.policyKind === "BASE"
        ? value.effectiveStartDate
        : ""
  };
}

export function useWorkPolicyForm() {
  const [value, setValue] = useState<WorkPolicyFormValue | null>(null);
  const [baseline, setBaseline] = useState("");

  const replace = (next: WorkPolicyFormValue) => {
    setValue(next);
    setBaseline(JSON.stringify(next));
  };

  return {
    value,
    dirty: useMemo(
      () => value !== null && JSON.stringify(value) !== baseline,
      [baseline, value]
    ),
    startNew: (kind: PolicyKind) => replace(createEmptyWorkPolicyForm(kind)),
    loadDetail: (detail: WorkPolicyDetail) => replace(workPolicyDetailToForm(detail)),
    setValue,
    setField: <K extends keyof WorkPolicyFormValue>(
      field: K,
      fieldValue: WorkPolicyFormValue[K]
    ) =>
      setValue(current =>
        current ? updateWorkPolicyFormField(current, field, fieldValue) : current
      ),
    markSaved: (next?: WorkPolicyFormValue) => {
      const saved = next ?? value;
      if (saved) {
        setValue(saved);
        setBaseline(JSON.stringify(saved));
      }
    }
  };
}

export function isBaseDetail(detail: WorkPolicyDetail): detail is BaseWorkPolicyDetail {
  return detail.policyKind === "BASE";
}
